<template>
    <kt-modal
        :visible="recordVisible"
        :title="recordTitle"
        @dismiss="onDismiss()"
        size="modal-lg"
    >
        <template #body>
            <work-form v-model="record"></work-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions">
                <button @click="onDismiss()" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
                <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger">
                    <Trash2 :size="16" class="me-1" />Delete
                </button>
                <button @click="saveRecord()" class="btn btn-primary">
                    <Save :size="16" class="me-1" />Save
                </button>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import WorkForm from "@/components/Work/WorkForm.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Save } from 'lucide-vue-next';
import { TimeModel } from "@/models/TaskModel";

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Work Entry");
const recordVisible = ref(true);

const props = defineProps({
    id: {
        type: [Number, String],
        default: null
    },
    requestId: {
        type: Number,
        default: 0
    },
    clientId: {
        type: Number,
        default: 0
    },
    techId: {
        type: Number,
        default: 0
    },
    workCompleteDate: {
        type: String,
        default: ''
    },
    isNew: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'saved']);

const unwatch: Array<Function> = [];

const newRecord = (): TimeModel => {
    const task = new TimeModel();
    task.Type = 'work';
    
    if (props.requestId) {
        task.RequestId = props.requestId;
    }
    
    if (props.clientId) {
        task.CustomerNumber = props.clientId;
    }
    
    if (props.techId) {
        task.TechId = props.techId;
    }
    
    // Pre-populate with workCompleteDate if provided (from calendar)
    if (props.workCompleteDate) {
        task.WorkCompleteDate = props.workCompleteDate;
    }
    
    return task;
};

const record = ref<TimeModel>(newRecord());

const saveRecord = async () => {
    try {
        record.value.Type = 'work';
        
        let data = Helper.prep(record.value);
        
        // Validation for required fields
        const errors: string[] = [];
        
        if (!data.AssignedTo) {
            errors.push('Technician is required');
        }
        if (!data.CustomerNumber) {
            errors.push('Client is required');
        }
        if (!data.RequestId) {
            errors.push('Request/Job is required');
        }
        if (!data.WorkCompleteDate) {
            errors.push('Work Date is required');
        }
        if (!data.ActualTime || parseFloat(data.ActualTime) <= 0) {
            errors.push('Hours Worked must be greater than 0');
        }
        
        if (errors.length > 0) {
            Alert.notify({
                title: 'Validation Error',
                text: errors.join('\n')
            });
            return;
        }
        
        console.debug("save work entry", data);
        
        data = await ApiService.saveTime(data);
        
        if (route.name && route.name.toString().includes('Work')) {
            Emitter.emit("redirect", "/work/edit/" + data.Id);
        } else {
            Alert.notify({
                text: "Work entry saved successfully!",
                type: "success"
            });
            Emitter.emit('work:saved', data);
            emit('saved', data);
            emit('close');
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () => {
    Alert.notify({
        text: "Deletion is currently disabled.",
        type: "warning"
    });
};

const onDismiss = () => {
    if (route.name && route.name.toString().includes('Work')) {
        router.back();
    } else {
        emit('close');
    }
};

const load = async () => {
    // Only use route.params.id if NOT opened as modal (no context props provided)
    // When opened as modal from JobForm, route.params.id contains the Job ID, not Time ID
    const isModalContext = props.requestId || props.clientId || props.techId;
    const id = props.id || (isModalContext ? null : route.params.id);

    if (id && id !== "new" && !props.isNew) {
        const data = await ApiService.loadTime(id as string);
        recordTitle.value = "Edit Work Entry";
        record.value = data;
    } else {
        recordTitle.value = "New Work Entry";
        record.value = newRecord();
    }
};

unwatch.push(watch(() => props.id, () => {
    load();
}, { immediate: true }));

unwatch.push(watch(() => route.params.id, () => {
    if (!props.id) {
        load();
    }
}, { immediate: true }));

onUnmounted(() => {
    unwatch.forEach(fn => fn());
});
</script>
